import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { LandingHeader } from '@/components/landing/LandingHeader';
import { LandingHero } from '@/components/landing/LandingHero';
import { LandingFeatures } from '@/components/landing/LandingFeatures';
import { LandingFAQ } from '@/components/landing/LandingFAQ';
import Footer from '@/components/Footer';
import { isRunningInApp } from '@/lib/appRedirect';

const Index = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // Redirect to auth only if running in iOS mobile app
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (isRunningInApp() && isIOS && !user) {
      navigate('/auth', { replace: true });
      return;
    }

    // Handle Google OAuth callback FIRST, even if user is logged in
    const googleConnected = searchParams.get('google_connected');
    const googleError = searchParams.get('google_error');
    const accessToken = searchParams.get('access_token');
    const refreshToken = searchParams.get('refresh_token');
    const expiresIn = searchParams.get('expires_in');
    const tokenType = searchParams.get('token_type');

    if (googleConnected === 'true' && accessToken) {
      // Store tokens in localStorage
      const tokenData = {
        access_token: accessToken,
        refresh_token: refreshToken || '',
        expires_at: Date.now() + (parseInt(expiresIn || '3600') * 1000),
        token_type: tokenType || 'Bearer'
      };
      
      localStorage.setItem('google_calendar_tokens', JSON.stringify(tokenData));
      
      // Check if this is a popup window
      if (window.opener) {
        // Send message to parent window and close popup
        window.opener.postMessage({ type: 'GOOGLE_OAUTH_SUCCESS' }, '*');
        window.close();
      } else {
        // Regular flow: show toast and redirect appropriately
        toast({
          title: "Connexion réussie",
          description: "Votre compte Google Calendar est maintenant connecté !",
        });
        
        // If user is logged in, go to dashboard
        if (user) {
          navigate('/dashboard', { replace: true });
        } else {
          navigate('/auth', { replace: true });
        }
      }
      return; // Exit early to prevent further navigation logic
    } else if (googleError) {
      let errorMessage = "Une erreur s'est produite lors de la connexion.";
      
      switch (googleError) {
        case 'access_denied':
          errorMessage = "L'autorisation Google Calendar a été refusée.";
          break;
        case 'missing_code':
          errorMessage = "Code d'autorisation manquant.";
          break;
        case 'token_exchange_failed':
          errorMessage = "Échec de l'échange de tokens.";
          break;
        case 'server_error':
          errorMessage = "Erreur serveur lors de la connexion.";
          break;
      }
      
      toast({
        title: "Erreur de connexion",
        description: errorMessage,
        variant: "destructive",
      });

      // Clean URL and redirect appropriately
      if (user) {
        navigate('/dashboard', { replace: true });
      } else {
        navigate('/auth', { replace: true });
      }
      return; // Exit early to prevent further navigation logic
    }

    // If user is already authenticated, redirect to dashboard
    if (user) {
      navigate('/dashboard', { replace: true });
    }
  }, [searchParams, navigate, toast, user]);

  // Show landing page for non-authenticated users
  return (
    <div className="min-h-screen flex flex-col">
      <LandingHeader />
      <main className="flex-1">
        <LandingHero />
        <LandingFeatures />
        <LandingFAQ />
      </main>
      <Footer />
    </div>
  );
};

export default Index;